import { AddToBag } from 'apps/widget/src/components/OverlayIcons/AddToBag';
import clsx from 'clsx';
import { MouseEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ClientReviewResponse, ImageSize, ProductWidget } from '@vestico/model';

import { ProductImage, sizeString, useProductLink, useProductName } from '@vestico/shared';
import globalStyle from '@vestico/shared/src/layouts/utils.module.css';

import { useRealTimePrice } from '../hooks/useRealTimePrice';
import style from './ProductsListItem.module.css';
import { ProductRealTimePrice } from './ProductsListItemPrice';

export interface ProductsListItemProps {
  response: ClientReviewResponse | undefined;
  product: ProductWidget | null | undefined;
  hasProduct: boolean;
  showProductLink?: boolean;
  onProductLinkClick?: (product: ProductWidget) => void;
  noShadow?: boolean;
}

export const ProductListItem = ({ response, product, hasProduct, showProductLink, onProductLinkClick, noShadow }: ProductsListItemProps) => {
  const { url, goToProduct } = useProductLink({ product, showProductLink, onProductLinkClick });
  const { enabled: realTimePriceEnabled, realTimePrice } = useRealTimePrice({ product });

  const name = useProductName(product);

  const { t } = useTranslation();

  const optionsString = useMemo(() => {
    const matchingProduct = response?.products.find((p) => p.documentID === product?.documentID);
    if (!matchingProduct) {
      return '';
    }
    const { size, variantName, variantUID } = matchingProduct;
    if (variantUID) {
      const variant = product?.variantUIDMap[variantUID]?.[0];
      if (variant) {
        const { size, otherOptions } = variant;
        const options = Object.keys(otherOptions).map((otherOptionKey) => `${otherOptionKey} ${otherOptions[otherOptionKey]}`);
        if (size) {
          options.splice(0, 0, sizeString(size));
        }
        return options.join(', ');
      }
    }
    // legacy
    if (size) {
      return sizeString(size);
    }
    return variantName ?? '';
  }, [product?.documentID, product?.variantUIDMap, response?.products]);

  const handleProductLinkClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      event.preventDefault();
      goToProduct();
    },
    [goToProduct]
  );

  const productImageMarkup = useMemo(() => {
    if (!hasProduct) {
      return null;
    }
    const image = (
      <div
        className={clsx(style.image, {
          [style.clickable]: showProductLink,
          [style.shadow]: !noShadow,
        })}
      >
        <ProductImage product={product} size={ImageSize.medium} />
      </div>
    );

    return url ? (
      <a href={url} className={clsx(style.linkContainer)} onClick={handleProductLinkClick}>
        {image}
      </a>
    ) : (
      image
    );
  }, [handleProductLinkClick, hasProduct, noShadow, product, showProductLink, url]);

  if (!hasProduct) {
    return null;
  }

  const imageTitle = (
    <span className={clsx(globalStyle.textNormal, globalStyle.textBold, globalStyle.textClampLine2)}>
      <span>{name}</span>
    </span>
  );

  return (
    <div className={clsx(style.root)}>
      {productImageMarkup}
      <div className={clsx(style.content)}>
        <div>
          {url ? <a className={clsx(style.noUnderline)}>{imageTitle}</a> : imageTitle}
          {optionsString.length > 0 ? (
            <div className={clsx(style.options)}>
              <span className={clsx(globalStyle.textBold, globalStyle.textSmall)}>{t('LOC_PRODUCT_INFO_FEATURED_VARIANTS')}</span>
              <span className={clsx(globalStyle.textSmall, style.option)}>{optionsString}</span>
            </div>
          ) : null}
        </div>
        <div className={clsx(style.section)}>
          {realTimePriceEnabled ? <ProductRealTimePrice price={realTimePrice} /> : null}
          <AddToBag response={response} product={product} buttonStyle="button" />
          {url && !realTimePriceEnabled ? (
            <div>
              <a className={clsx(style.link)} href={url} onClick={handleProductLinkClick}>
                {t('LOC_VIEW_PRODUCT')}
              </a>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
